import { AnimationController } from "@/core/battle/AnimationController";
import { CombatCalculator } from "@/core/battle/CombatCalculator";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { EntityRegistry } from "@/core/battle/EntityRegistry";
import { PathCalculator } from "@/core/battle/PathCalculator";
import { SoundManager } from "@/core/battle/SoundManager";
import { TurnManager } from "@/core/battle/TurnManager";
import { BattleUnit, Skill, CombatResult, FactionType, BattleStatus, ControlState } from "@/core/types/battle.types";
import { coordsToId, idToCoords } from "@/core/utils/hex.utils";
import { BattleBus } from "@/events/Events";
import { useBattleStore } from "@/store/useBattleStore";

/**
 * Executes combat actions
 * Handles attack calculations, animations, and state updates
 */
export class CombatExecutor {
    private currentAbortController: AbortController | null = null;
    
    constructor(
        private animationController: AnimationController,
        private registry: EntityRegistry,
        private turnManager: TurnManager,
    ) {}
    
    /**
     * Execute skill attack
     * Handles state transitions, animations, and cleanup
     * Works for both player and enemy units
     */
    async execute(
        attackerId: string,
        targetId: string,
        skillId: string,
        options?: { forcedHit?: boolean, opportunityAttack: boolean }
    ): Promise<void> {
        // Cancel any previous combat
        // this.cancelCurrentCombat();
        
        // Create abort controller for this combat
        this.currentAbortController = new AbortController();
        const signal = this.currentAbortController.signal;
        
        const state = useBattleStore.getState();
        
        const attacker = state.units.get(attackerId);
        const target = state.units.get(targetId);
        
        if (!attacker || !target) {
            console.warn('[CombatExecutor] Attacker or target not found');
            return;
        }
        
        const skill = attacker.skills.find(s => s.id === skillId);
        if (!skill) {
            console.warn('[CombatExecutor] Skill not found');
            return;
        }

        const isOpportunityStrike = options?.opportunityAttack !== undefined ? true : false;
        
        // Validate stamina
        if (!isOpportunityStrike && attacker.currentStamina < skill.cost) {
            console.warn('[CombatExecutor] Not enough stamina');
            return;
        }


        // Determine if this is player or enemy combat
        // const isPlayerUnit = attacker.faction === FactionType.Player;
        // if (isPlayerUnit) {
        //     const targetHexId = coordsToId(target);
        //     if (!state.targetHexes.includes(targetHexId)) {
        //         console.warn('[CombatExecutor] Unit not in range');
        //         return;
        //     }
        // }
        
        
        // Enemy combat happens during EnemyTurn, don't change state
        
        try {
            // Calculate result
            const attackerHexId = coordsToId(attacker);
            const targetHexId = coordsToId(target);
            const attackerTerrain = state.battleMap.get(attackerHexId);
            const targetTerrain = state.battleMap.get(targetHexId);
            const result = CombatCalculator.calculateAttack(attacker, target, skill, attackerTerrain?.terrain, targetTerrain?.terrain, options);
            
            // Get visuals
            const attackerVisual = this.registry.getUnit(attackerId);
            const targetVisual = this.registry.getUnit(targetId);
            
            if (!attackerVisual || !targetVisual) {
                console.warn('[CombatExecutor] Visual not found');
                return;
            }
            
            BattleBus.emit('STAMINA_DRAIN_START', {
                unitId: attackerId,
                amount: skill.cost
            });
            
            SoundManager.playAttackSound(result.isMiss, skill.range);
            // Animate
            await this.animationController.playSkillAnimation(
                result,
                attackerVisual,
                targetVisual,
                { signal, isOpportunityStrike }
            );
            
            // Apply results (only if not aborted)
            if (!signal.aborted) {
                
                this.applySkillResult(attackerId, targetId, skill, result, isOpportunityStrike);

            }
            
        } catch (err: any) {
            if (signal.aborted || err.message?.includes('aborted')) {
                console.log('[CombatExecutor] Combat cancelled');
            } else {
                console.error('[CombatExecutor] Combat failed:', err);
            }
        } finally {
            // Cleanup
            this.currentAbortController = null;
            
            
            // For enemy, state remains EnemyTurn (managed by AI/TurnManager)
        }
    }
    
    /**
     * Cancel current combat animation
     */
    cancelCurrentCombat(): void {
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }
    }
    
    private applySkillResult(
        attackerId: string, // Передаем ID вместо объекта
        targetId: string,   // Передаем ID вместо объекта
        skill: Skill,
        result: CombatResult,
        isOpportunityStrike: boolean
    ): void {
        const state = useBattleStore.getState();
        const currentAttacker = state.units.get(attackerId);
        const currentTarget = state.units.get(targetId);

        if (!currentAttacker || !currentTarget) return;

        // 1. Обработка цели (Урон или Смерть)
        if (result.isDead) {
            // TurnManager сам вызовет атомарный finalizeUnitDeath в сторе
            this.turnManager.handleUnitDeath(targetId);
        } else {
            state.updateUnit(targetId, {
                count: result.finalDefenderCount,
                currentUnitHp: result.finalDefenderHp
            });
        }
        
        // 2. Обновление атакующего (Stamina)
        const newStamina = isOpportunityStrike ? currentAttacker.currentStamina : currentAttacker.currentStamina - skill.cost;
        BattleBus.emit('STAMINA_DRAIN_STOP', {
            unitId: currentAttacker.unitId,
            amount: newStamina
        });
        // if (!isOpportunityStrike) {
            state.updateUnit(attackerId, {
                currentStamina: newStamina,
                opportunityAttackUsed: currentAttacker.opportunityAttackUsed || isOpportunityStrike,
            });
        // }

        // if (currentAttacker.faction === FactionType.Player) {
        //     // 3. Обновление достижимых гексов (зависит от новой Stamina)
        //     const updatedAttacker = useBattleStore.getState().units.get(attackerId);
        //     if (updatedAttacker) {
        //         const resPath = PathCalculator.getReachableNodes(
        //             updatedAttacker, 
        //             { currentStamina: newStamina, speed: currentAttacker.stats.speed },
        //             useBattleStore.getState().battleMap // Берем свежую карту (вдруг цель умерла и гекс пуст)
        //         );
        //         state.setReachableHexes(resPath.reachableIds, resPath.reachableMap);
        //     }
        // }
    }
    
    
    
}